// GeoNewsHero.jsx — pinned geopolitics hero for the Indices page.
// Features the latest article in the geo category (big image + headline), with a
// side list of the next few. Real WP REST data (same source/shape as MagmaNewsSection).
// Mobile-safe by construction: flex-wrap + aspect-ratio, no percentage-height traps.

import React, { useState, useEffect } from 'react';

const WP_API  = '/api/wp/wp-json/rr/v1/noticias';
const RR_SITE = 'https://relatorioreservado.com.br';

// WP category slug for the geopolitics feed. Adjust to match your taxonomy.
const GEO_CATEGORY = 'geopolitica';

const GOLD = '#D4AF37';

// ── helpers (mirrored from MagmaNewsSection) ──
function fixImageUrl(url) {
  if (!url) return null;
  return url.replace('/api/wp/wp-content', 'https://relatorioreservado.com.br/wp-content');
}
function decodeEntities(html) {
  if (!html) return '';
  if (typeof document !== 'undefined') {
    const t = document.createElement('textarea');
    t.innerHTML = html;
    return t.value;
  }
  return html;
}
function formatDate(str) {
  if (!str) return '';
  const d = new Date(str);
  const diff = Math.floor((Date.now() - d) / 3600000);
  if (diff < 1) return 'Agora';
  if (diff < 24) return `${diff}h atrás`;
  const days = Math.floor(diff / 24);
  if (days === 1) return 'Ontem';
  if (days < 7) return `${days} dias atrás`;
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' });
}
const articleUrl = (id) => `${RR_SITE}/noticia/${id}`;

export function GeoNewsHero({ theme, category = GEO_CATEGORY, label = 'Geopolítica · O que move os índices', offset = 0, variant = 'hero' }) {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    let alive = true;
    const fetchMapped = async (url) => {
      const res  = await fetch(url);
      const data = await res.json();
      return (data.articles || []).map(a => ({
        ...a,
        title:     decodeEntities(a.title),
        excerpt:   decodeEntities(a.excerpt),
        thumbnail: fixImageUrl(a.thumbnail),
      }));
    };
    const load = async () => {
      try {
        // Try the category first; if the slug doesn't match the taxonomy (or has
        // no posts), fall back to latest news so the section is never empty.
        let mapped = await fetchMapped(`${WP_API}?per_page=12&category=${encodeURIComponent(category)}&_=${Date.now()}`);
        if (!mapped.length) {
          mapped = await fetchMapped(`${WP_API}?per_page=12&_=${Date.now()}`);
        }
        if (!alive) return;
        setArticles(mapped);
        setLoading(false);
      } catch { setLoading(false); }
    };
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => { alive = false; clearInterval(id); };
  }, [category]);

  if (loading) return (
    <div style={{ padding: '2rem 0', fontFamily: "'IBM Plex Mono',monospace", fontSize: 11,
      color: theme.textSecondary, letterSpacing: '0.15em' }}>
      CARREGANDO GEOPOLÍTICA…
    </div>
  );

  const windowed = articles.slice(offset);
  if (!windowed.length) return null;

  const SectionLabel = () => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.25rem' }}>
      <div style={{ width: 3, height: 24, background: `linear-gradient(180deg, ${GOLD}, rgba(212,175,55,0))`, borderRadius: 2 }} />
      <span style={{ fontSize: '1.4rem', fontWeight: 900, color: theme.textPrimary, letterSpacing: '-0.02em' }}>{label}</span>
      <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700, color: GOLD,
        border: `1px solid rgba(212,175,55,0.4)`, padding: '3px 8px', borderRadius: 2, letterSpacing: '0.12em' }}>RR</span>
      <a href={RR_SITE} target="_blank" rel="noopener noreferrer"
        style={{ marginLeft: 'auto', color: theme.textSecondary, textDecoration: 'none', fontSize: '0.85rem',
          borderBottom: `1px solid ${theme.border}`, paddingBottom: 1 }}>
        Ver acervo ↗
      </a>
    </div>
  );

  // GRID variant — simple 3-up cards, no big hero (for continuation feeds)
  if (variant === 'grid') {
    const cards = windowed.slice(0, 6);
    return (
      <div style={{ marginBottom: '2rem' }}>
        <SectionLabel />
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem' }}>
          {cards.map((a) => (
            <a key={a.id} href={articleUrl(a.id)} target="_blank" rel="noopener noreferrer"
              style={{ flex: '1 1 260px', minWidth: 0, textDecoration: 'none',
                background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 12, overflow: 'hidden' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
              <div style={{ aspectRatio: '16/9', width: '100%',
                background: a.thumbnail ? `url(${a.thumbnail}) center/cover` : 'linear-gradient(135deg,#1a1020,#2a1a2a)' }} />
              <div style={{ padding: '0.9rem 1rem 1rem' }}>
                {a.categories?.[0] && (
                  <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 8, fontWeight: 700,
                    letterSpacing: '0.12em', color: GOLD, textTransform: 'uppercase', marginBottom: 5 }}>{a.categories[0]}</div>
                )}
                <div style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 700, fontSize: 14,
                  color: theme.textPrimary, lineHeight: 1.25,
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{a.title}</div>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: theme.textSecondary, marginTop: 6 }}>{formatDate(a.date)}</div>
              </div>
            </a>
          ))}
        </div>
      </div>
    );
  }

  const lead = windowed[0];
  const side = windowed.slice(1, 5);

  return (
    <div style={{ marginBottom: '3rem' }}>
      <SectionLabel />

      {/* hero row: lead (flex 2) + side list (flex 1), wraps on mobile */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem' }}>

        {/* LEAD — big pinned article */}
        <a href={articleUrl(lead.id)} target="_blank" rel="noopener noreferrer"
          style={{ flex: '2 1 340px', minWidth: 0, textDecoration: 'none',
            background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 16, overflow: 'hidden',
            display: 'block' }}>
          <div style={{
            aspectRatio: '16/9', width: '100%',
            background: lead.thumbnail ? `url(${lead.thumbnail}) center/cover` : 'linear-gradient(135deg,#1a1020,#2a1a2a)',
            position: 'relative',
          }}>
            <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.85), rgba(0,0,0,0.1) 55%, transparent)' }} />
            <div style={{ position: 'absolute', left: 0, right: 0, bottom: 0, padding: '1.5rem' }}>
              {lead.categories?.[0] && (
                <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, fontWeight: 700,
                  letterSpacing: '0.14em', color: '#000', background: GOLD, textTransform: 'uppercase',
                  padding: '3px 8px', borderRadius: 3, display: 'inline-block', marginBottom: 10 }}>
                  {lead.categories[0]}
                </span>
              )}
              <h2 style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 800,
                fontSize: 'clamp(1.3rem, 2.6vw, 2rem)', color: '#fff', lineHeight: 1.15,
                letterSpacing: '-0.02em', margin: 0 }}>
                {lead.title}
              </h2>
            </div>
          </div>
          {lead.excerpt && (
            <p style={{ padding: '1rem 1.25rem 0.25rem', margin: 0, color: theme.textSecondary,
              fontSize: '0.95rem', lineHeight: 1.5,
              display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {lead.excerpt}
            </p>
          )}
          <div style={{ padding: '0.5rem 1.25rem 1.1rem', fontFamily: "'IBM Plex Mono',monospace",
            fontSize: 10, color: theme.textSecondary, letterSpacing: '0.06em' }}>
            {formatDate(lead.date)}
          </div>
        </a>

        {/* SIDE LIST — next few */}
        <div style={{ flex: '1 1 260px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 12 }}>
          {side.map((a) => (
            <a key={a.id} href={articleUrl(a.id)} target="_blank" rel="noopener noreferrer"
              style={{ textDecoration: 'none', display: 'flex', gap: 12, alignItems: 'flex-start',
                background: theme.cardBg, border: `1px solid ${theme.border}`, borderRadius: 10, padding: 10 }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'rgba(212,175,55,0.4)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}>
              <div style={{ width: 84, height: 60, flexShrink: 0, borderRadius: 6,
                background: a.thumbnail ? `url(${a.thumbnail}) center/cover` : 'linear-gradient(135deg,#1a1020,#2a1a2a)' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                {a.categories?.[0] && (
                  <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 8, fontWeight: 700,
                    letterSpacing: '0.12em', color: GOLD, textTransform: 'uppercase', marginBottom: 3 }}>
                    {a.categories[0]}
                  </div>
                )}
                <div style={{ fontFamily: "'Roble',system-ui,sans-serif", fontWeight: 700, fontSize: 13,
                  color: theme.textPrimary, lineHeight: 1.25,
                  display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {a.title}
                </div>
                <div style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 9, color: theme.textSecondary, marginTop: 4 }}>
                  {formatDate(a.date)}
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export default GeoNewsHero;